import Fuse from 'fuse.js';
import axios from 'axios';

let entityCache = {
    drivers: null,
    cars: null,
    concessions: null,
    lastFetch: null,
    cacheTimeout: 5 * 60 * 1000,
};

async function getEntityData(store, entityType) {
    const now = Date.now();

    if (
        entityCache[entityType] &&
        entityCache.lastFetch &&
        now - entityCache.lastFetch < entityCache.cacheTimeout
    ) {
        return entityCache[entityType];
    }

    if (store) {
        let storeData;
        if (entityType === 'drivers' || entityType === 'employees') {
            storeData =
                store.state.employees && store.state.employees.length > 0
                    ? store.state.employees
                    : store.state.drivers;
        } else {
            storeData = store.state[entityType];
        }

        if (storeData && storeData.length > 0) {
            entityCache[entityType] = storeData;
            entityCache.lastFetch = now;
            return storeData;
        }
    }

    try {
        let url;
        if (entityType === 'drivers' || entityType === 'employees') {
            url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/drivers`;
        } else if (entityType === 'cars') {
            url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/cars`;
        } else {
            return [];
        }

        const result = await axios.get(url, {
            withCredentials: true,
        });

        const data = result.data || [];

        entityCache[entityType] = data;
        entityCache.lastFetch = now;
        return data;
    } catch (error) {
        return [];
    }
}

export async function searchDrivers(query, store) {
    if (!query || !query.trim()) {
        return [];
    }

    const drivers = await getEntityData(store, 'drivers');
    if (!drivers || drivers.length === 0) {
        return [];
    }

    const fuse = new Fuse(drivers, {
        keys: [
            { name: 'name', weight: 0.7 },
            { name: 'value', weight: 0.7 },
            { name: 'label', weight: 0.6 },
            { name: 'id', weight: 0.2 },
            { name: 'driverNumber', weight: 0.1 },
        ],
        threshold: 0.3,
        includeScore: true,
    });

    const results = fuse.search(query);
    return results.map((result) => ({
        ...result.item,
        score: result.score,
        matchScore: result.score,
    }));
}

export async function searchCars(query, store) {
    if (!query || !query.trim()) {
        return [];
    }

    const cars = await getEntityData(store, 'cars');
    if (!cars || cars.length === 0) {
        return [];
    }

    const normalizedQuery = query.replace(/\s+/g, '').toUpperCase();

    const fuse = new Fuse(cars, {
        keys: [
            { name: 'licenseNumber', weight: 0.8 },
            { name: 'licensePlate', weight: 0.8 },
            { name: 'id', weight: 0.1 },
            { name: 'model', weight: 0.1 },
        ],
        threshold: 0.4,
        includeScore: true,
    });

    const results = fuse.search(normalizedQuery);
    return results.map((result) => ({
        ...result.item,
        score: result.score,
        matchScore: result.score,
    }));
}

export async function searchConcessions(query, store) {
    if (!query || !query.trim()) {
        return [];
    }

    const concessions = store?.state?.concessions || [];
    if (!concessions || concessions.length === 0) {
        return [];
    }

    const fuse = new Fuse(concessions, {
        keys: [
            { name: 'value', weight: 0.8 },
            { name: 'label', weight: 0.2 },
        ],
        threshold: 0.3,
        includeScore: true,
    });

    const results = fuse.search(query);
    return results.map((result) => ({
        ...result.item,
        score: result.score,
        matchScore: result.score,
    }));
}

export async function searchAllEntities(query, store) {
    if (!query || !query.trim()) {
        return {
            drivers: [],
            cars: [],
            concessions: [],
        };
    }

    const [drivers, cars, concessions] = await Promise.all([
        searchDrivers(query, store),
        searchCars(query, store),
        searchConcessions(query, store),
    ]);

    return {
        drivers: drivers.slice(0, 5),
        cars: cars.slice(0, 5),
        concessions: concessions.slice(0, 5),
    };
}

export async function getEntityDetails(entityType, entityId, store) {
    const entities = await getEntityData(store, entityType);
    return entities.find((e) => {
        if (entityType === 'drivers' || entityType === 'employees') {
            return e.id === entityId || e.driverNumber === entityId || e.uuid === entityId;
        } else if (entityType === 'cars') {
            return (
                e.id === entityId ||
                e.uuid === entityId ||
                e.licenseNumber === entityId ||
                e.licensePlate === entityId
            );
        }
        return false;
    });
}

export function clearEntityCache() {
    entityCache = {
        drivers: null,
        cars: null,
        concessions: null,
        lastFetch: null,
        cacheTimeout: 5 * 60 * 1000,
    };
}

export function createEntityCommand(entityType, entity, context) {
    const { router, route } = context;

    if (entityType === 'drivers' || entityType === 'employees') {
        const iconHtml = '<i class="ri-user-line"></i>';
        const driverName = entity.name || entity.value || entity.label || '';
        const driverNumber = entity.driverNumber || entity.driverId || entity.id;
        const driverId = entity.id || entity.driverNumber || entity.driverId;

        const keywordsArray = [driverName, driverNumber, driverId].filter(Boolean);

        return {
            id: `entity-driver-${driverId}`,
            title: driverName || `Fahrer #${driverNumber || driverId}`,
            subtitle: driverNumber ? `Fahrer #${driverNumber}` : `ID: ${driverId}`,
            section: 'Mitarbeiter',
            category: 'Entität',
            icon: iconHtml,
            keywords: keywordsArray.join(' '),
            hotkey: '',
            entityType: 'driver',
            entityId: driverId || driverNumber,
            entityName: driverName,
            entityData: entity,
            handler: () => {},
        };
    } else if (entityType === 'cars') {
        const licenseNumber = entity.licenseNumber || entity.licensePlate || entity.value;
        const iconHtml = '<i class="ri-car-line"></i>';
        const keywordsArray = [licenseNumber, entity.model, entity.make, entity.id].filter(Boolean);
        return {
            id: `entity-car-${entity.id || licenseNumber}`,
            title: licenseNumber || `Fahrzeug #${entity.id}`,
            subtitle: entity.model || entity.make || '',
            section: 'Fahrzeuge',
            category: 'Entität',
            icon: iconHtml,
            keywords: keywordsArray.join(' '),
            hotkey: '',
            entityType: 'car',
            entityId: entity.id || entity.uuid || licenseNumber,
            entityName: licenseNumber,
            entityData: entity,
            handler: () => {},
        };
    } else if (entityType === 'concessions') {
        const iconHtml = '<i class="ri-building-line"></i>';
        const keywordsArray = [entity.value, entity.label].filter(Boolean);
        return {
            id: `entity-concession-${entity.value || entity.id}`,
            title: `Konzession #${entity.value || entity.id}`,
            subtitle: entity.label || '',
            section: 'Konzessionen',
            category: 'Entität',
            icon: iconHtml,
            keywords: keywordsArray.join(' '),
            hotkey: '',
            entityType: 'concession',
            entityId: entity.value || entity.id,
            entityName: `Concession #${entity.value || entity.id}`,
            entityData: entity,
            handler: () => {},
        };
    }

    return null;
}
